import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Heart, ShieldCheck, Award, GraduationCap, FileCheck2, Lock } from "lucide-react";
import { NAVICA, NAV_ITEMS } from "@/lib/constants";
import { NavicaMark, NavicaSeal, CulturalFlagsStrip, WeLoveNdisBadge } from "@/components/brand/NavicaBrand";

const COMPLIANCE_BADGES = [
  { label: "AASW", icon: Award },
  { label: "NDIS Worker Screening", icon: ShieldCheck },
  { label: "Working with Children Check", icon: GraduationCap },
  { label: "Professional Indemnity Insured", icon: FileCheck2 },
];

export default function Footer() {
  return (
    <footer className="bg-[#1A363D] text-[#F0E5DE] mt-24" data-testid="site-footer">
      {/* Decorative wave top */}
      <div className="h-12 navica-wave" aria-hidden="true" style={{ transform: "scaleY(-1)" }} />

      {/* Compliance band */}
      <div className="border-b border-white/10" data-testid="compliance-band">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-7 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {COMPLIANCE_BADGES.map((b) => (
            <div key={b.label} className="flex items-center gap-3" data-testid={`badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                <b.icon className="w-4 h-4 text-[#EA823A]" />
              </div>
              <div className="text-xs uppercase tracking-wider text-white/80 leading-tight">{b.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Brand + contact */}
        <div>
          <div className="flex items-center gap-3 mb-5">
            <NavicaMark size={44} />
            <div>
              <div className="font-display text-2xl text-white">NAVICA</div>
              <div className="text-xs tracking-[0.2em] italic text-[#F4C3CC]">supports</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-[#C9D2D5] mb-4 max-w-xs">
            AASW-accredited social work and NDIS support coordination. Guiding people through life&rsquo;s complex chapters.
          </p>
          <p className="text-xs italic text-[#F4C3CC] mb-5">&ldquo;{NAVICA.seal}&rdquo;</p>
          <div className="space-y-3 text-sm">
            <a href={`mailto:${NAVICA.email}`} className="flex items-center gap-3 hover:text-[#E2718A] transition" data-testid="footer-email">
              <Mail className="w-4 h-4 text-[#EA823A]" /> {NAVICA.email}
            </a>
            <a href={`tel:${NAVICA.phoneRaw}`} className="flex items-center gap-3 hover:text-[#E2718A] transition" data-testid="footer-phone">
              <Phone className="w-4 h-4 text-[#EA823A]" /> {NAVICA.phone}
            </a>
            <div className="flex items-start gap-3" data-testid="footer-address">
              <MapPin className="w-4 h-4 text-[#EA823A] mt-0.5 shrink-0" />
              <span>{NAVICA.address}</span>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-display text-lg text-white mb-5">Quick Links</h4>
          <ul className="space-y-3 text-sm">
            {NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className="hover:text-[#E2718A] transition-colors"
                  data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/trust" className="hover:text-[#E2718A] transition-colors" data-testid="footer-link-trust">
                Trust & Compliance
              </Link>
            </li>
            <li>
              <Link to="/resources" className="hover:text-[#E2718A] transition-colors" data-testid="footer-link-resources">
                Important Resources & Community Contacts
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="hover:text-[#E2718A] transition-colors" data-testid="footer-link-privacy">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>

        {/* Business details */}
        <div data-testid="footer-business-details">
          <h4 className="font-display text-lg text-white mb-5">Business details</h4>
          <dl className="space-y-3 text-sm">
            <Detail label="Legal name" value={NAVICA.legal_name} />
            <Detail label="ABN" value={NAVICA.abn} />
            <Detail label="AASW #" value={NAVICA.aasw_number} />
            <Detail label="NDIS Provider" value={NAVICA.ndis_provider_status} />
          </dl>
          <Link to="/trust" className="inline-flex items-center gap-1.5 mt-5 text-xs uppercase tracking-wider text-[#EA823A] hover:text-[#E2718A] font-semibold" data-testid="footer-view-compliance">
            View full credentials →
          </Link>
        </div>

        {/* Acknowledgment */}
        <div>
          <h4 className="font-display text-lg text-white mb-5">Acknowledgment</h4>
          <p className="text-sm leading-relaxed text-[#C9D2D5] mb-4">
            Navica Supports acknowledges Aboriginal and Torres Strait Islander peoples as the Traditional Custodians of the lands where we live and work. Sovereignty was never ceded.
          </p>
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-block w-8 h-5 rounded-sm" style={{ background: "linear-gradient(to bottom, #000 0 33%, #E2718A 33% 66%, #EA823A 66% 100%)" }} aria-label="Aboriginal flag" />
            <span className="inline-block w-8 h-5 rounded-sm bg-gradient-to-b from-emerald-700 via-white to-blue-700" aria-label="Torres Strait Islander flag" />
            <span className="text-xs text-[#C9D2D5]">Always was, always will be.</span>
          </div>
          <p className="text-sm leading-relaxed text-[#C9D2D5] mb-5">
            Inclusive of LGBTIQA+ communities, their families and allies.
          </p>
          <div className="flex items-center gap-3">
            <a href={NAVICA.social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E2718A] flex items-center justify-center transition" data-testid="footer-facebook">
              <Facebook className="w-4 h-4" />
            </a>
            <a href={NAVICA.social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E2718A] flex items-center justify-center transition" data-testid="footer-instagram">
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Cultural safety strip */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">In this space</div>
            <CulturalFlagsStrip className="text-white/80" showLabels={false} />
            <div className="text-xs text-[#C9D2D5] italic max-w-md">
              We acknowledge the Traditional Custodians of the land we work on and pay respect to Elders past and present. LGBTQIA+ safe.
            </div>
          </div>
          <WeLoveNdisBadge />
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#9CA9AD]">
          <div>© {new Date().getFullYear()} {NAVICA.legal_name}. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <Link
              to="/staff/login"
              className="flex items-center gap-1.5 hover:text-[#E2718A] transition group"
              data-testid="footer-staff-login"
            >
              <Lock className="w-3 h-3 group-hover:text-[#E2718A]" />
              <span className="uppercase tracking-wider">Staff login</span>
            </Link>
            <div className="flex items-center gap-1.5">
              Made with <Heart className="w-3 h-3 text-[#E2718A] fill-[#E2718A]" /> in Sydney
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] uppercase tracking-wider text-[#9CA9AD]">{label}</dt>
      <dd className="text-[#F0E5DE]">{value}</dd>
    </div>
  );
}
